import { useState } from "react";
import { AlertOctagon, Clock, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const PRESETS = [
  { id: 30, label: "30 min" },
  { id: 60, label: "1 h" },
  { id: 120, label: "2 h" },
  { id: "custom", label: "Personnalisé" },
];

export default function OrangeConfigDialog({
  open,
  onOpenChange,
  address,
  clientName,
  submitting,
  onSubmit,
}) {
  const [selected, setSelected] = useState(30);
  const [custom, setCustom] = useState(45);

  const delay = selected === "custom" ? Math.max(5, Math.min(720, parseInt(custom, 10) || 0)) : selected;
  const canSubmit = Number.isInteger(delay) && delay >= 5 && delay <= 720 && !submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="orange-config-dialog"
        className="max-w-[440px] rounded-[20px]"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span
              className="flex h-8 w-8 items-center justify-center rounded-full"
              style={{ background: "#FEF3C7", color: "#B45309" }}
            >
              <AlertOctagon size={16} />
            </span>
            <DialogTitle className="text-[20px] font-semibold text-slate-900">
              Configurer le feu orange
            </DialogTitle>
          </div>
          <DialogDescription className="mt-1 text-[13.5px] text-slate-500 leading-snug">
            L'équipe sera alertée immédiatement. Un rappel sera envoyé aux
            superviseurs à l'expiration du délai.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 rounded-[12px] border border-slate-200 bg-[var(--sa-subtle)] px-4 py-3 text-[13px]">
          <p className="font-semibold text-slate-700">Adresse</p>
          <p className="mt-0.5 text-slate-600">{address || "—"}</p>
          {clientName ? (
            <>
              <p className="mt-2 font-semibold text-slate-700">Client</p>
              <p className="mt-0.5 text-slate-600">{clientName}</p>
            </>
          ) : null}
        </div>

        <div className="mt-2">
          <p className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-slate-500">
            Délai avant rappel
          </p>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p.id)}
                data-testid={`delay-${p.id}`}
                className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold transition ${
                  selected === p.id
                    ? "bg-[#F59E0B] text-white shadow-sm"
                    : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <Clock size={12} />
                {p.label}
              </button>
            ))}
          </div>

          {selected === "custom" ? (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                min={5}
                max={720}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="delay-custom-input"
                className="sa-input"
                style={{ maxWidth: 120, height: 44 }}
              />
              <span className="text-[13px] text-slate-500">minutes (entre 5 et 720)</span>
            </div>
          ) : null}
        </div>

        <DialogFooter className="mt-3 flex gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="flex-1 rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            data-testid="orange-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => onSubmit(delay)}
            disabled={!canSubmit}
            className="flex-1 rounded-[14px] px-5 py-3 text-[14px] font-semibold text-white transition disabled:opacity-70"
            style={{
              background: "#F59E0B",
              boxShadow: "0 4px 14px -4px rgba(245,158,11,0.5)",
            }}
            data-testid="orange-submit"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Envoi…
              </span>
            ) : (
              `Déclencher (${delay} min)`
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
